import styled from '@emotion/styled';
import { Box, Card } from '@mui/material';
import Image from 'next/image';
import ReactPlayer from 'react-player';
import { BodyLarge, LabelLarge, Row, TitleMedium } from '../../../styles';
import { Breakpoint, isCompact } from '../../../types';
import { shouldForwardProps } from '../../../utils';

const PostContainer = styled(Card, shouldForwardProps)<{
    breakpoint: Breakpoint;
}>`
    gap: ${({ theme }) => theme.spacing(2)};
    border-radius: ${({ breakpoint, theme }) => (isCompact(breakpoint) ? 0 : theme.spacing(1.5))};
    padding: ${({ theme, breakpoint }) =>
        isCompact(breakpoint) ? theme.spacing(2) : theme.spacing(3)};
    position: relative;
`;

const PostTitle = styled(TitleMedium, shouldForwardProps)`
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PostText = styled(BodyLarge, shouldForwardProps)`
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
    white-space: pre-line;
`;

const FullSizeImageFrame = styled(Box, shouldForwardProps)`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const FullSizeImageContainer = styled(Box)`
    position: relative;
`;

const FullSizeImage = styled(Image)`
    object-fit: cover;
    border-radius: 4px;
`;

const SourceContainer = styled(Row)`
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.black};
    color: ${({ theme }) => theme.colors.white};
    padding: ${({ theme }) => theme.spacing(1)};
    border-top-left-radius: 16px;
    opacity: 0.5;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const SourceText = styled(LabelLarge)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Gallery = styled(Box, shouldForwardProps)`
    position: relative;
`;

const ArrowContainer = styled(Box, shouldForwardProps)<{ arrow: 'left' | 'right' }>`
    position: absolute;
    display: flex;
    justify-content: center;
    height: 100%;
    top: 0;
    right: ${({ arrow }) => (arrow === 'right' ? 0 : 'auto')};
    left: ${({ arrow }) => (arrow === 'left' ? 0 : 'auto')};
    background-color: ${({ theme }) => theme.colors.surface};
`;

const ImagesList = styled(Row)`
    gap: ${({ theme }) => theme.spacing(2)};
    overflow: hidden;
    -webkit-transition: all 0.5s ease-in-out;
    transition: all 0.5s ease-in-out;
`;

const GalleryItem = styled(Box, shouldForwardProps)<{ isSelected: boolean }>`
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0.5)};
    display: flex;
    align-items: center;
`;

const GalleryImage = styled(Image)`
    object-fit: cover;
    border-radius: 4px;
`;

const GalleryVideo = styled(ReactPlayer)`
    pointer-events: none;
    .react-player__preview {
        border-radius: 4px;
    }
`;

const AuthorContainer = styled(Row, shouldForwardProps)<{ answersOpen: boolean }>`
    border-bottom: ${({ answersOpen, theme }) =>
        answersOpen ? `1px solid ${theme.colors.surfaceVariant}` : 'none'};
    padding-bottom: ${({ answersOpen, theme }) => (answersOpen ? theme.spacing(3) : 0)};
`;

const CommentsButtonsGrid = styled(Box)`
    display: grid;
    grid-template-columns: 1fr 2fr 2fr;
    align-items: end;
    border-top: ${({ theme }) => `${theme.colors.surfaceVariant} 1px solid`};
    padding-left: ${({ theme }) => theme.spacing(5)};
    padding-top: ${({ theme }) => theme.spacing(2)};
`;

export {
    ArrowContainer,
    AuthorContainer,
    CommentsButtonsGrid,
    FullSizeImage,
    FullSizeImageContainer,
    FullSizeImageFrame,
    Gallery,
    GalleryImage,
    GalleryItem,
    GalleryVideo,
    ImagesList,
    PostContainer,
    PostText,
    PostTitle,
    SourceContainer,
    SourceText
};
